import { effect, Injectable, signal } from '@angular/core';
import { ActiveFilter, ColumnDef } from '@shared/components/display-table-filter/display-table-filter.component';
import { SelectionModel } from '@angular/cdk/collections';
import { finalize } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { SpinnerService } from '@shared/spinner/spinner.service';
import { clearDashboardFilters, setDashboardPage, setDashboardProperty } from '@store/admin/admin.actions';
import { Store } from '@ngrx/store';
import * as fromApp from '@store';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { PageEvent } from '@angular/material/paginator';

@Injectable({
  providedIn: 'root',
})
export class DisplayTableService {
  pageName: string;

  editMode = false;
  selection = new SelectionModel<any>(true, []);
  queryItemServiceCall: any;

  paginationTotal = 40;

  totalItems = 0;
  offset = 0;
  currentPage = 1;

  filterQuery = '';
  sortQuery = '';
  dataSource: MatTableDataSource<any>;
  pageSizeOptions = [20, 40, 100, 99999]

  sortStates = {
    untouched: 'untouched',
    ascending: 'asc',
    descending: 'desc',
  };

  columnData: ColumnDef[] = [];
  // inputs that will change
  readonly _sortBy = signal<[string, string][]>([]);
  readonly sortBy = this._sortBy.asReadonly();

  setSortBy(value: [string, string][]) {
    this._sortBy.set(value);
  }

  readonly _displayedColumns = signal<string[]>([])
  readonly displayedColumns = this._displayedColumns.asReadonly();

  setDisplayedColumns(columns: string[]) {
    this._displayedColumns.set(columns);
  }

  readonly _activeFilters = signal<ActiveFilter[]>([])
  readonly activeFilters = this._activeFilters.asReadonly();

  setActiveFilters(filters: ActiveFilter[]) {
    this._activeFilters.set(filters);
  }


  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly spinnerService: SpinnerService
  ) {
    effect(() => {
      const filters = this.activeFilters();
      if (filters != null) {
        this.applyNewFilter();
      }
    });
  }

  queryItems() {
    this.spinnerService.show('app-spinner');
    this.queryItemServiceCall(this.getFullQueryString()).pipe(finalize(() => this.spinnerService.hide('app-spinner')))
      .subscribe(results => {
        this.selection.clear();
        this.totalItems = results.total;
        this.dataSource = new MatTableDataSource(results.results);
      });
  }

  getBaseQueryString(): string {
    return `?count=${this.paginationTotal}&offset=${this.offset}`;
  }

  getFullQueryString(): string {
    return this.getBaseQueryString() + this.sortQuery + this.filterQuery;
  }

  newDashboardSelected(filters: any) {
    this.setUpAdminFilter(filters);
    this.queryItems();
  }

  setUpAdminFilter(state: any) {
    let page = state.find(p => p.page === this.pageName);
    // get the initial filtered site list
    this.setActiveFilters(page?.activeFilters || []);
    this.setFilterQueryByCurrentActiveFilters();
    this.setSortBy(page?.sortBy || []);
    this.offset = page?.offset || 0;
    this.currentPage = page?.currentPage || 1;
    this.paginationTotal = page?.paginationTotal || 40;

    // set up the sort indicators
    this.setSortQueryByCurrentActiveSort();
  }

  setFilterQueryByCurrentActiveFilters() {
    this.filterQuery = this.activeFilters().length > 0 ? '&filters=' + this.activeFilters().map(f => f.query)
      .join(',') : '';
  }

  setSortQueryByCurrentActiveSort() {
    const sorts = this.sortBy()
      .map(([col, dir]) => `${col}(${dir})`)
      .join(',');
    this.sortQuery = this.sortBy().length > 0 ? `&sort=${sorts}` : '';
  }

  clearFilters(queryItems: boolean = true): void {
    if (this.getBaseQueryString() !== this.getFullQueryString()) {
      this.setActiveFilters([]);
      this.filterQuery = '';
      this.sortQuery = '';
      this.setSortBy([]);
      this.store.dispatch(clearDashboardFilters({ page: this.pageName }));
      if (queryItems) {
        this.queryItems();
      }
    }
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource ? this.dataSource.data.length : 0;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle(): void {
    this.isAllSelected() ? this.selection.clear() : this.dataSource.data.forEach(row => this.selection.select(row));
  }

  reorderColumns($event: any) {
    let tempColumns = [...this.displayedColumns()];
    moveItemInArray(tempColumns, $event.previousIndex, $event.currentIndex);
    this.setDisplayedColumns(tempColumns);
  }

  removeColumn(columnName: string) {
    this.setDisplayedColumns(this.displayedColumns().filter(col => col !== columnName));
  }

  toggleMultiSort(column: string): void {
    let i = this.sortBy().findIndex(([col]) => col === column);
    if (i == -1) {
      this.setSortBy([...this.sortBy(), [column, this.sortStates.untouched]]);
      i = this.sortBy().length - 1;
    }

    const current = this.sortBy()[i][1];
    if (current === this.sortStates.untouched) {
      this.setSortBy(this.sortBy().map(([col, dir]) =>
        col === column ? [column, this.sortStates.ascending] : [col, dir]
      ));
    } else if (current === this.sortStates.ascending) {
      this.setSortBy(this.sortBy().map(([col, dir]) =>
        col === column ? [column, this.sortStates.descending] : [col, dir]
      ));
    } else {
      this.setSortBy(this.sortBy().filter(([col]) => col !== column));
    }

    this.setSortQueryByCurrentActiveSort();
    this.store.dispatch(
      setDashboardProperty({
        property: 'sortBy',
        value: this.sortBy(),
        page: this.pageName,
      })
    );
    this.queryItems();
  }

  goToPage(pageEvent: PageEvent) {
    if (this.paginationTotal !== pageEvent.pageSize) {
      this.currentPage = 1;
    } else {
      this.currentPage = (pageEvent.pageIndex + 1);
    }
    this.paginationTotal = pageEvent.pageSize;
    this.offset = (this.currentPage - 1) * this.paginationTotal;
    this.store.dispatch(
      setDashboardPage({
        offset: this.offset,
        paginationTotal: this.paginationTotal,
        currentPage: this.currentPage,
        page: this.pageName,
      })
    );
    this.queryItems();
  }

  applyNewFilter(): void {
    let oldFilter = this.filterQuery;
    this.setFilterQueryByCurrentActiveFilters();
    this.store.dispatch(
      setDashboardProperty({
        property: 'activeFilters',
        value: this.activeFilters(),
        page: this.pageName,
      })
    );
    if (oldFilter !== this.filterQuery) {
      this.resetOffsetAndCurrentPage();
      this.queryItems();
    }
  }

  resetOffsetAndCurrentPage(): void {
    this.offset = 0;
    this.currentPage = 1;
    this.store.dispatch(
      setDashboardPage({
        offset: this.offset,
        currentPage: this.currentPage,
        paginationTotal: this.paginationTotal,
        page: this.pageName,
      })
    );
  }
}
